/**
 * @module lib/search/analytics
 */

import isNil from "lodash/isNil";
import { getProp } from "utils/utils";

/**
 * @method sendAnalyticsEvent
 * @description adds data to the analytics layer.
 * @param {Object} c - collection of search data.
 * @param {String} c.type - can be "search page" (for results shown on a dedicated page), "modal" for results shown inside modal, "financial symbol search" for specific financial symbols search tools.
 * @param {String} c.mode - value of mode parameter used for search.
 * @param {String} [c.page=1] - value of the search results page.
 * @param {String} c.query - searh query.
 * @param {Boolean} [c.isLs=false] - set to true if results shown based on Livesuggest feature.
 * @param {Boolean} [c.isDym=false] - set to true if results shown based on "Did You Mean" feature.
 * @param {String} c.grouping - is grouping/facet of search results shown by default for non-stock results or when person clicks on specific tab/facet (I.e. tab name - "by articles", "by author", "by section").
 * @param {Number} [c.total=0] -  is the number of total search results (excluding stocks) or 0 (zero) if no results.
 * @param {Number} [c.resultsShown=0] -  is a number indicating how many search(non-stock) results are shown at the time or 0 (zero) if no results, of how how many faceted items are displayed.
 * @param {Number} [c.securitiesTotal=0] - is a total number of securities found that match search criteria or 0 (zero) if no results.
 * @param {Number} [c.securitiesShown=0] - s a number of securities displayed at the time search is performed or 0 (zero) if no results.
 */
export function sendAnalyticsEvent({
  type,
  mode,
  page = 1,
  query,
  isLs = false,
  isDym = false,
  grouping,
  total = 0,
  securitiesTotal = 0 }) {
  let facetItemsNum;
  if (grouping === "by author") {
    facetItemsNum = getProp(window, "tgam.datalayer.elasticSearch.authors").length;
  } else if (grouping === "by section") {
    facetItemsNum = getProp(window, "tgam.datalayer.elasticSearch.sections").length;
  }

  const event = {
    "event": "searchEvent",
    "searchEvent": {
      "type": type || "search page",
      "mode": mode || "all",
      "page": page || 1,
      "query": query,
      "isLiveSuggest": isLs,
      "isDidYouMean": isDym || false,
      "grouping": grouping,
      "total": mode !== "securities" ? total : undefined,
      "shown": mode !== "securities" ? Math.min(total, 10) : (grouping === "by author" || grouping === "by section") ? facetItemsNum : undefined,
      "securities": page === 1 ? securitiesTotal : undefined,
      "shownSecurities": page === 1 ? Math.min(securitiesTotal, 2) : undefined
    }
  };

  // without setTimeout the search event will be pushed to the analytics layer twice,
  // as it’s trying to fire the event when the event is not ready,
  // with setTimeout we are making sure that the bootstrap is ready and the event is prepared
  setTimeout(() => window.analyticsLayer.push(event), 500);
}

/**
 * @method getAnalytics
 * @description get and send the search analytics
 */
export function getAnalytics() {
  if (isNil(window.tgam.datalayer.search)) {
    return;
  }
  const searchData = {
    mode: getProp(window, "tgam.datalayer.search.mode"),
    page: getProp(window, "tgam.datalayer.search.page"),
    query: getProp(window, "tgam.datalayer.search.query"),
    grouping: "by article",
    total: parseInt(getProp(window, "tgam.datalayer.search.total"), 10),
    securitiesTotal: parseInt(getProp(window, "tgam.datalayer.search.securitiesTotal"), 10),
  };

  /* We only send searchEvent when a user performs the initial search (load page with results),
  when a user clicks on a link or pagination or navigate to additional search result pages -
  having data-analyticsclick is on a link sufficient */
  if (searchData.page === 1) {
    sendAnalyticsEvent(searchData);
  }
}
